// import { useState, useEffect, useCallback } from "react";
// import useInfiniteScroll from "react-infinite-scroll-hook";

// // Define types for the API response
// interface MetaData {
//   current_page: number;
//   last_page: number;
// }

// interface APIResponse<T> {
//   data: T[];
//   meta: MetaData;
// }

// interface UseInfiniteScrollFetcherProps<T> {
//   fetchData: (params: any) => Promise<{ data: APIResponse<T> }>;
//   initialParams: any;
// }

// function useInfiniteScrollRequest<T>({
//   fetchData,
//   initialParams,
// }: UseInfiniteScrollFetcherProps<T>) {
//   const [items, setItems] = useState<T[]>([]);
//   const [params, setParams] = useState(initialParams);
//   const [hasMore, setHasMore] = useState(true);
//   const [loading, setLoading] = useState(false);

//   const loadData = useCallback(
//     async (currentParams: any) => {
//       if (loading) return;

//       setLoading(true);
//       try {
//         const response = await fetchData(currentParams);
//         const { data, meta } = response?.data;

//         // If it's the first page, replace items; otherwise append
//         setItems(
//           currentParams.page === 1
//             ? data
//             : (prevItems) => [...prevItems, ...data]
//         );
//         setHasMore(meta?.current_page < meta?.last_page);

//         // Only increment page if this was a successful load
//         if (meta?.current_page < meta?.last_page) {
//           setParams((prev: any) => ({ ...prev, page: prev.page + 1 }));
//         }
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setHasMore(false);
//       } finally {
//         setLoading(false);
//       }
//     },
//     [fetchData] // Remove loading from dependencies
//   );

//   // Initial load effect
//   useEffect(() => {
//     const initialLoad = async () => {
//       await loadData({ ...initialParams, page: 1 });
//     };
//     initialLoad();
//   }, [loadData]); // Remove JSON.stringify(initialParams) from dependencies

//   const fetchMoreData = useCallback(() => {
//     if (!hasMore || loading) return;
//     loadData(params);
//   }, [loadData, params, hasMore, loading]);

//   const updateParams = useCallback(
//     (newParams: Partial<typeof initialParams>) => {
//       const updatedParams = {
//         ...initialParams,
//         ...newParams,
//         page: 1,
//       };
//       setParams(updatedParams);
//       setItems([]);
//       setHasMore(true);
//       loadData(updatedParams);
//     },
//     [initialParams, loadData]
//   );

//   const refetch = useCallback(() => {
//     const refetchParams = {
//       ...params,
//       page: 1,
//     };
//     setParams(refetchParams);
//     setItems([]);
//     setHasMore(true);
//     loadData(refetchParams);
//   }, [params, loadData]);

//   const [sentryRef] = useInfiniteScroll({
//     loading,
//     hasNextPage: hasMore,
//     onLoadMore: fetchMoreData,
//     disabled: false,
//     rootMargin: "0px 0px 400px 0px",
//   });

//   return { items, sentryRef, loading, updateParams, refetch, hasMore };
// }

// export default useInfiniteScrollRequest;

import { useInfiniteQuery } from "@tanstack/react-query";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useState, useCallback, useMemo } from "react";

// Define types for the API response
interface MetaData {
  current_page: number;
  last_page: number;
}

interface APIResponse<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
}

interface UseInfiniteScrollRequestProps<T> {
  queryKey: string; // Added queryKey to the interface
  fetchData: (params: any) => Promise<{ data: APIResponse<T> }>;
  initialParams: any;
}

function useInfiniteScrollRequest<T>({
  queryKey,
  fetchData,
  initialParams,
}: UseInfiniteScrollRequestProps<T>) {
  const [params, setParams] = useState(initialParams);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [queryKey, params],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await fetchData({
        ...params,
        page: pageParam,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.current_page < lastPage?.last_page) {
        return lastPage?.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array of items
  const items = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data]);

  // Update params and reset query
  const updateParams = useCallback(
    (newParams: Partial<typeof initialParams>) => {
      setParams((prev: any) => ({
        ...prev,
        ...newParams,
        page: 1,
      }));
    },
    []
  );

  const loading = isLoading || isFetchingNextPage;

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: loading,
  });

  return {
    items,
    sentryRef,
    loading,
    updateParams,
    refetch,
  };
}

export default useInfiniteScrollRequest;

